import {I18n} from '@aws-amplify/core';

import {SimplePageHeader} from '@flexinet/ui-components';

import {useVerifiedPermissions} from '@flexinet/auth-components';
import {rbacActions} from '../../../config/rbac/actions';
import {ReloadRecordsButton} from '../buttons/ReloadRecordsButton';
import {FilterRecordsButton} from '../buttons/FilterRecordsButton';
import {DeleteRecordButton} from '../buttons/DeleteRecordButton';
import {AddRecordButton} from '../buttons/AddRecordButton';

/**
 * FNP-918: the index page toolbar.
 *
 * ⚠️ This is a HOOK and must be called as one. It was previously invoked
 * inline in the JSX below (`tools={useTools(...)}`), which works only because
 * the call happens unconditionally on every render — it is a rules-of-hooks
 * violation that lint would flag as soon as anything conditional appeared
 * around it. It is now called in the component body.
 *
 * Every tool is RBAC-gated by ABSENCE, not by `disabled` (§2, §10): a disabled
 * button still advertises an action this operator will never be allowed to
 * take. Reload and Filter are ungated — both only re-read data the operator can
 * already see.
 */
const useTools = (selectedIds: string[], onDeleteComplete?: () => void) => {

    const {allowedActions} = useVerifiedPermissions();

    const tools = [
        <ReloadRecordsButton key='reload'/>,
        <FilterRecordsButton key='filter'/>,
    ];

    if (allowedActions.includes(rbacActions.DeleteSetupIntent)) {
        tools.push(
            <DeleteRecordButton
                key='delete'
                selectedIds={selectedIds}
                onDeleteComplete={onDeleteComplete}
            />,
        );
    }

    if (allowedActions.includes(rbacActions.CreateSetupIntent)) {
        tools.push(<AddRecordButton key='add'/>);
    }

    return tools;
};

interface IndexPageHeaderProps {
    selectedIds?: string[];
    onDeleteComplete?: () => void;
}

export const IndexPageHeader = ({
                                    selectedIds = [],
                                    onDeleteComplete,
                                }: IndexPageHeaderProps) => {

    const tools = useTools(selectedIds, onDeleteComplete);

    return (
        <SimplePageHeader
            title={I18n.get('Setup Intents')}
            tools={tools}
        />
    );
};
